import { 
  format, 
  parse, 
  addDays, 
  addWeeks, 
  addMonths, 
  startOfMonth, 
  setDate,
  isBefore,
  isAfter,
  formatDistanceToNow,
  subDays,
  addYears,
  isSameDay
} from 'date-fns';

/**
 * Calculates the next occurrence date based on frequency and current date
 * @param {Date|string} startDate - The start date of the recurrence
 * @param {string} frequency - One of: daily, weekly, monthly, firstOfMonth, specificDayOfMonth
 * @param {Object} options - Additional options like dayOfMonth
 * @returns {Date} The next occurrence date
 */
export const calculateNextOccurrence = (startDate, frequency, options = {}) => {
  const baseDate = typeof startDate === 'string' ? new Date(startDate) : startDate;
  
  // Return the start date if it's invalid
  if (isNaN(baseDate.getTime())) {
    return new Date();
  }
  
  const { dayOfWeek, dayOfMonth } = options;
  
  switch (frequency) {
    case 'daily':
      return addDays(baseDate, 1);
      
    case 'weekly':
      return addWeeks(baseDate, 1);
      
    case 'biweekly':
      return addWeeks(baseDate, 2);
      
    case 'monthly':
      // Move to the next month
      let nextMonth = addMonths(baseDate, 1);
      
      // If day of month is specified, set that specific day
      if (dayOfMonth && dayOfMonth >= 1 && dayOfMonth <= 31) {
        const daysInMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate();
        const actualDay = Math.min(dayOfMonth, daysInMonth);
        nextMonth.setDate(actualDay);
      }
      
      return nextMonth;
      
    case 'quarterly':
      return addMonths(baseDate, 3);
      
    case 'semiannually':
      return addMonths(baseDate, 6);
      
    case 'annually':
      return addYears(baseDate, 1);
      
    default:
      return addMonths(baseDate, 1);
  }
};

/**
 * Get a human-readable description of a recurrence
 * @param {Object} recurrence - The recurrence object
 * @returns {string} Human-readable description
 */
export const getRecurrenceDescription = (recurrence) => {
  if (!recurrence) {
    return 'Invalid recurrence';
  }
  
  const { frequency, dayOfWeek, dayOfMonth } = recurrence;
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  switch (frequency) {
    case 'daily':
      return 'Every day';
      
    case 'weekly':
      const day = dayOfWeek !== undefined && dayOfWeek >= 0 && dayOfWeek <= 6 
        ? daysOfWeek[dayOfWeek] 
        : 'week';
      return `Every ${day}`;
      
    case 'biweekly':
      return 'Every two weeks';
      
    case 'monthly':
      if (dayOfMonth) {
        return `On day ${dayOfMonth} of every month`;
      }
      return 'Every month';
      
    case 'quarterly':
      return 'Every three months';
      
    case 'semiannually':
      return 'Every six months';
      
    case 'annually':
      return 'Once a year';
      
    default:
      return frequency || 'Custom schedule';
  }
};

/**
 * Get the status of a recurrence
 * @param {Object} recurrence - The recurrence object
 * @returns {Object} Status information
 */
export const getRecurrenceStatus = (recurrence) => {
  if (!recurrence) {
    return { status: 'error', label: 'Invalid recurrence', color: 'error' };
  }
  
  const now = new Date();
  const { startDate, isActive } = recurrence;
  
  if (!isActive) {
    return { status: 'inactive', label: 'Inactive', color: 'default' };
  }
  
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  
  // Check if start date is valid
  if (isNaN(start.getTime())) {
    return { status: 'error', label: 'Invalid date', color: 'error' };
  }
  
  // Upcoming - start date is in the future
  if (isBefore(now, start)) {
    const timeUntil = formatDistanceToNow(start);
    return {
      status: 'upcoming',
      label: `Upcoming (starts in ${timeUntil})`,
      color: 'info'
    };
  }
  
  // Check if it has a last execution date
  if (recurrence.lastExecuted) {
    const lastExec = typeof recurrence.lastExecuted === 'string' 
      ? new Date(recurrence.lastExecuted) 
      : recurrence.lastExecuted;
    
    // If last execution was less than a day ago
    if (isBefore(lastExec, now) && isBefore(now, addDays(lastExec, 1))) {
      return {
        status: 'recentlyExecuted',
        label: 'Recently executed',
        color: 'success'
      };
    }
  }
  
  // Active and ready
  return { status: 'active', label: 'Active', color: 'success' };
};

/**
 * Format a recurrence for display
 * @param {Object} recurrence - The recurrence object
 * @returns {Object} Formatted recurrence with display properties
 */
export const formatRecurrenceForDisplay = (recurrence) => {
  if (!recurrence) {
    return {
      id: 'error',
      name: 'Invalid recurrence',
      description: '',
      schedule: 'N/A',
      status: 'error',
      statusLabel: 'Error',
      statusColor: 'error',
      nextOccurrence: null,
      nextOccurrenceFormatted: 'N/A'
    };
  }
  
  const status = getRecurrenceStatus(recurrence);
  const nextOccurrence = recurrence.nextScheduled || calculateNextOccurrence(recurrence.startDate, recurrence.frequency, {
    dayOfWeek: recurrence.dayOfWeek,
    dayOfMonth: recurrence.dayOfMonth
  });
  
  return {
    id: recurrence._id || recurrence.id || 'unknown',
    name: recurrence.name || 'Unnamed Recurrence',
    description: recurrence.description || '',
    schedule: getRecurrenceDescription(recurrence),
    status: status.status,
    statusLabel: status.label,
    statusColor: status.color,
    nextOccurrence,
    nextOccurrenceFormatted: nextOccurrence ? format(new Date(nextOccurrence), 'MMM d, yyyy') : 'N/A'
  };
};

/**
 * Get color scheme for a status
 * @param {string} status - Status value
 * @returns {Object} Color scheme details
 */
export const getStatusColorScheme = (status) => {
  switch (status) {
    case 'active':
      return {
        color: 'success',
        textColor: 'green.600',
        bgColor: 'green.100',
        iconColor: 'green.500'
      };
      
    case 'inactive':
    case 'paused':
      return {
        color: 'default',
        textColor: 'gray.600',
        bgColor: 'gray.100',
        iconColor: 'gray.500'
      };
      
    case 'upcoming':
      return {
        color: 'info',
        textColor: 'blue.600',
        bgColor: 'blue.100',
        iconColor: 'blue.500'
      };
      
    case 'completed':
      return {
        color: 'secondary',
        textColor: 'purple.600',
        bgColor: 'purple.100',
        iconColor: 'purple.500'
      };
      
    case 'error':
      return {
        color: 'error',
        textColor: 'red.600',
        bgColor: 'red.100',
        iconColor: 'red.500'
      };
      
    default:
      return {
        color: 'default',
        textColor: 'gray.600',
        bgColor: 'gray.100',
        iconColor: 'gray.500'
      };
  }
};

/**
 * Format recurrence schedule in a human-readable form
 * @param {Object} recurrence - Recurrence object
 * @returns {string} Formatted schedule
 */
export const formatRecurrenceSchedule = (recurrence) => {
  if (!recurrence || !recurrence.frequency) {
    return 'Invalid schedule';
  }
  
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  switch (recurrence.frequency) {
    case 'daily':
      return 'Every day';
      
    case 'weekly':
      const day = recurrence.dayOfWeek !== undefined && daysOfWeek[recurrence.dayOfWeek]
        ? daysOfWeek[recurrence.dayOfWeek]
        : 'week';
      return `Every ${day}`;
      
    case 'biweekly':
      return 'Every two weeks';
      
    case 'monthly':
      if (recurrence.dayOfMonth) {
        return `Monthly on day ${recurrence.dayOfMonth}`;
      }
      return 'Monthly';
      
    default:
      return recurrence.frequency.charAt(0).toUpperCase() + recurrence.frequency.slice(1);
  }
};

/**
 * Format recurrence status for display
 * @param {boolean} isActive - Whether the recurrence is active
 * @returns {Object} Status information
 */
export const formatRecurrenceStatus = (isActive) => {
  if (isActive) {
    return { statusText: 'Active', color: 'success' };
  } else {
    return { statusText: 'Inactive', color: 'default' };
  }
};

/**
 * Get the next occurrence date for a recurrence
 * @param {Object} recurrence - Recurrence object
 * @returns {Date|null} Next occurrence date
 */
export const getNextOccurrenceDate = (recurrence) => {
  if (!recurrence || !recurrence.startDate) {
    return null;
  }
  
  // If nextScheduled is already calculated, use it
  if (recurrence.nextScheduled) {
    return new Date(recurrence.nextScheduled);
  }
  
  // Otherwise, calculate based on recurrence settings
  const startDate = new Date(recurrence.startDate);
  const now = new Date();
  
  // If start date is in the future, that's the next occurrence
  if (startDate > now) {
    return startDate;
  }
  
  // Calculate based on frequency
  let nextDate;
  const { frequency, dayOfWeek, dayOfMonth } = recurrence;
  
  switch (frequency) {
    case 'daily':
      // Find next day after today
      nextDate = new Date(now);
      nextDate.setDate(nextDate.getDate() + 1);
      break;
      
    case 'weekly':
      // Find next occurrence of the specified day of week
      nextDate = new Date(now);
      const currentDay = nextDate.getDay();
      const targetDay = dayOfWeek !== undefined ? dayOfWeek : startDate.getDay();
      let daysToAdd = targetDay - currentDay;
      if (daysToAdd <= 0) daysToAdd += 7; // Ensure we go to next week if today is the day
      nextDate.setDate(nextDate.getDate() + daysToAdd);
      break;
      
    case 'biweekly':
      // Similar to weekly but every two weeks
      // This is simplified - a real implementation would track even/odd weeks
      nextDate = new Date(now);
      const biweeklyCurrentDay = nextDate.getDay();
      const biweeklyTargetDay = dayOfWeek !== undefined ? dayOfWeek : startDate.getDay();
      let biweeklyDaysToAdd = biweeklyTargetDay - biweeklyCurrentDay;
      if (biweeklyDaysToAdd <= 0) biweeklyDaysToAdd += 7;
      nextDate.setDate(nextDate.getDate() + biweeklyDaysToAdd + 7); // Add an extra week
      break;
      
    case 'monthly':
      // Find next occurrence of the specified day of month
      nextDate = new Date(now);
      nextDate.setMonth(nextDate.getMonth() + 1);
      const targetDate = dayOfMonth !== undefined ? dayOfMonth : startDate.getDate();
      nextDate.setDate(1); // Go to first of month
      const daysInMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
      nextDate.setDate(Math.min(targetDate, daysInMonth)); // Set to target day (or last day if target exceeds month length)
      break;
      
    case 'quarterly':
      // Every three months
      nextDate = new Date(now);
      nextDate.setMonth(nextDate.getMonth() + 3);
      if (dayOfMonth) {
        nextDate.setDate(1);
        const quarterlyDaysInMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
        nextDate.setDate(Math.min(dayOfMonth, quarterlyDaysInMonth));
      }
      break;
      
    case 'semiannually':
      // Every six months
      nextDate = new Date(now);
      nextDate.setMonth(nextDate.getMonth() + 6);
      if (dayOfMonth) {
        nextDate.setDate(1);
        const semiannualDaysInMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
        nextDate.setDate(Math.min(dayOfMonth, semiannualDaysInMonth));
      }
      break;
      
    case 'annually':
      // Once per year
      nextDate = new Date(now);
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      if (dayOfMonth) {
        nextDate.setDate(1);
        const annualDaysInMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
        nextDate.setDate(Math.min(dayOfMonth, annualDaysInMonth));
      }
      break;
      
    default:
      // Default to monthly if unknown frequency
      nextDate = new Date(now);
      nextDate.setMonth(nextDate.getMonth() + 1);
  }
  
  return nextDate;
};

/**
 * Generate preview dates based on recurrence settings
 * @param {Object} options - Recurrence options
 * @param {string} options.frequency - The frequency type
 * @param {number} options.dayOfWeek - The day of week (0-6)
 * @param {number} options.dayOfMonth - The day of month (1-31)
 * @param {Date|string} options.startDate - The start date
 * @param {number} count - Number of preview dates to generate
 * @returns {Array<Date>} Array of preview dates
 */
export const generatePreviewDates = (options, count = 4) => {
  const { frequency, dayOfWeek, dayOfMonth, startDate } = options;
  
  if (!startDate || !frequency) {
    return [];
  }
  
  const start = new Date(startDate);
  if (isNaN(start.getTime())) {
    return [];
  }
  
  const result = [];
  let currentDate = new Date(start);
  
  for (let i = 0; i < count; i++) {
    if (i === 0) {
      // First date is the start date
      result.push(new Date(currentDate));
      continue;
    }
    
    switch (frequency.toLowerCase()) {
      case 'daily':
        currentDate.setDate(currentDate.getDate() + 1);
        break;
        
      case 'weekly':
        currentDate.setDate(currentDate.getDate() + 7);
        break;
        
      case 'biweekly':
        currentDate.setDate(currentDate.getDate() + 14);
        break;
        
      case 'monthly':
        // Move to the next month
        let nextMonth = currentDate.getMonth() + 1;
        let nextYear = currentDate.getFullYear();
        
        if (nextMonth > 11) {
          nextMonth = 0;
          nextYear++;
        }
        
        // Get the appropriate day, adjusting for month length
        const targetDay = dayOfMonth || currentDate.getDate();
        const newDate = new Date(nextYear, nextMonth, 1);
        const daysInMonth = new Date(nextYear, nextMonth + 1, 0).getDate();
        const adjustedDay = Math.min(targetDay, daysInMonth);
        
        newDate.setDate(adjustedDay);
        currentDate = newDate;
        break;
        
      case 'bimonthly':
        let biMonthlyNextMonth = currentDate.getMonth() + 2;
        let biMonthlyNextYear = currentDate.getFullYear();
        
        if (biMonthlyNextMonth > 11) {
          biMonthlyNextMonth -= 12;
          biMonthlyNextYear++;
        }
        
        const biMonthlyTargetDay = dayOfMonth || currentDate.getDate();
        const biMonthlyNewDate = new Date(biMonthlyNextYear, biMonthlyNextMonth, 1);
        const biMonthlyDaysInMonth = new Date(biMonthlyNextYear, biMonthlyNextMonth + 1, 0).getDate();
        const biMonthlyAdjustedDay = Math.min(biMonthlyTargetDay, biMonthlyDaysInMonth);
        
        biMonthlyNewDate.setDate(biMonthlyAdjustedDay);
        currentDate = biMonthlyNewDate;
        break;
        
      case 'quarterly':
        let quarterlyNextMonth = currentDate.getMonth() + 3;
        let quarterlyNextYear = currentDate.getFullYear();
        
        if (quarterlyNextMonth > 11) {
          quarterlyNextMonth -= 12;
          quarterlyNextYear++;
        }
        
        const quarterlyTargetDay = dayOfMonth || currentDate.getDate();
        const quarterlyNewDate = new Date(quarterlyNextYear, quarterlyNextMonth, 1);
        const quarterlyDaysInMonth = new Date(quarterlyNextYear, quarterlyNextMonth + 1, 0).getDate();
        const quarterlyAdjustedDay = Math.min(quarterlyTargetDay, quarterlyDaysInMonth);
        
        quarterlyNewDate.setDate(quarterlyAdjustedDay);
        currentDate = quarterlyNewDate;
        break;
        
      case 'semiannually':
        let semiAnnuallyNextMonth = currentDate.getMonth() + 6;
        let semiAnnuallyNextYear = currentDate.getFullYear();
        
        if (semiAnnuallyNextMonth > 11) {
          semiAnnuallyNextMonth -= 12;
          semiAnnuallyNextYear++;
        }
        
        const semiAnnuallyTargetDay = dayOfMonth || currentDate.getDate();
        const semiAnnuallyNewDate = new Date(semiAnnuallyNextYear, semiAnnuallyNextMonth, 1);
        const semiAnnuallyDaysInMonth = new Date(semiAnnuallyNextYear, semiAnnuallyNextMonth + 1, 0).getDate();
        const semiAnnuallyAdjustedDay = Math.min(semiAnnuallyTargetDay, semiAnnuallyDaysInMonth);
        
        semiAnnuallyNewDate.setDate(semiAnnuallyAdjustedDay);
        currentDate = semiAnnuallyNewDate;
        break;
        
      case 'annually':
        currentDate.setFullYear(currentDate.getFullYear() + 1);
        break;
        
      default:
        // Unknown frequency, just add a month
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    result.push(new Date(currentDate));
  }
  
  return result;
};

/**
 * Check if a recurrence is due for execution
 * @param {Object} recurrence - Recurrence object
 * @param {Date} referenceDate - Reference date to check against
 * @returns {boolean} Whether the recurrence is due
 */
export const isRecurrenceDue = (recurrence, referenceDate = new Date()) => {
  if (!recurrence || !recurrence.isActive) {
    return false;
  }
  
  // If nextScheduled is explicitly set and is in the past
  if (recurrence.nextScheduled) {
    const nextScheduled = new Date(recurrence.nextScheduled);
    return nextScheduled <= referenceDate;
  }
  
  // Otherwise, calculate based on the start date and frequency
  const startDate = new Date(recurrence.startDate);
  
  // Not due if start date is in the future
  if (startDate > referenceDate) {
    return false;
  }
  
  // If there's a last execution date, check if enough time has passed for next execution
  if (recurrence.lastExecuted) {
    const lastExecuted = new Date(recurrence.lastExecuted);
    const nextDue = calculateNextOccurrence(lastExecuted, recurrence.frequency, {
      dayOfWeek: recurrence.dayOfWeek,
      dayOfMonth: recurrence.dayOfMonth
    });
    
    return nextDue <= referenceDate;
  }
  
  // If no last execution, it's due if start date is today or in the past
  return startDate <= referenceDate;
};

/**
 * Get frequency options for dropdowns
 * @returns {Array} Array of frequency options
 */
export const getFrequencyOptions = () => [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly (every 3 months)' },
  { value: 'semiannually', label: 'Semi-Annually (every 6 months)' },
  { value: 'annually', label: 'Annually (yearly)' }
];

/**
 * Get day of week options for dropdowns
 * @returns {Array} Array of day options
 */
export const getDayOfWeekOptions = () => [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

/**
 * Format a date for display
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return 'Invalid date';
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a recurrence frequency for display
 * @param {Object} options - Frequency options
 * @param {string} options.frequency - The frequency type (daily, weekly, monthly, etc.)
 * @param {number} options.dayOfWeek - The day of week (0-6)
 * @param {number} options.dayOfMonth - The day of month (1-31)
 * @returns {string} Formatted frequency string
 */
export const formatFrequency = ({ frequency, dayOfWeek, dayOfMonth }) => {
  if (!frequency) return 'Not set';
  
  switch (frequency.toLowerCase()) {
    case 'daily':
      return 'Every day';
      
    case 'weekly':
      if (dayOfWeek !== undefined && dayOfWeek >= 0 && dayOfWeek <= 6) {
        const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `Every ${DAYS_OF_WEEK[dayOfWeek]}`;
      }
      return 'Every week';
      
    case 'monthly':
      if (dayOfMonth !== undefined) {
        // Handle special cases for day of month
        if (dayOfMonth === 1) {
          return 'On the 1st of every month';
        } else if (dayOfMonth === 2) {
          return 'On the 2nd of every month';
        } else if (dayOfMonth === 3) {
          return 'On the 3rd of every month';
        } else if (dayOfMonth === 21) {
          return 'On the 21st of every month';
        } else if (dayOfMonth === 22) {
          return 'On the 22nd of every month';
        } else if (dayOfMonth === 23) {
          return 'On the 23rd of every month';
        } else if (dayOfMonth === 31) {
          return 'On the 31st of every month';
        } else if (dayOfMonth >= 1 && dayOfMonth <= 31) {
          return `On the ${dayOfMonth}th of every month`;
        }
      }
      return 'Every month';
      
    case 'biweekly':
      return 'Every two weeks';
      
    case 'bimonthly':
      return 'Every two months';
      
    case 'quarterly':
      return 'Every three months';
      
    case 'semiannually':
      return 'Every six months';
      
    case 'annually':
      return 'Once a year';
      
    default:
      return frequency;
  }
};

export default {
  calculateNextOccurrence,
  getRecurrenceDescription,
  getRecurrenceStatus,
  formatRecurrenceForDisplay,
  getStatusColorScheme,
  formatRecurrenceSchedule,
  formatRecurrenceStatus,
  getNextOccurrenceDate,
  generatePreviewDates,
  getFrequencyOptions,
  getDayOfWeekOptions,
  formatDate,
  formatFrequency,
  isRecurrenceDue
}; 